import React, { useState } from "react";
import { Newspaper, ArrowRight, X, ZoomIn, ExternalLink } from "lucide-react";

const LA_SICILIA_LOGO = "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/pdythf0f_image.png";

/**
 * Add new press mentions here — the first item is the featured one,
 * the rest appear as smaller cards in a strip below.
 */
const PRESS_ARTICLES = [
  {
    id: "la-sicilia-stella-esse-2026",
    outlet: "La Sicilia",
    logo: LA_SICILIA_LOGO,
    date: "Lunedì 22 Giugno 2026",
    pageRef: "pag. 46",
    headline: "Roccaro premiato a Roma con La Stella di E.S.S.E.",
    image: "https://customer-assets.emergentagent.com/job_mare-method-shop-1/artifacts/hd5vd99k_articolo.png",
    externalUrl: null,            // set to the lasicilia.it article URL when available
    body: [
      "Con grande orgoglio condivido l'articolo pubblicato dal prestigioso quotidiano LA SICILIA, che ha dedicato uno spazio al mio percorso professionale e al riconoscimento ricevuto a Roma con il premio \"La Stella di E.S.S.E.\".",
      "L'articolo evidenzia il mio impegno nel campo dell'innovazione digitale, della divulgazione tecnologica e della formazione, attraverso progetti dedicati alla crescita delle persone e allo sviluppo di una maggiore consapevolezza nell'utilizzo degli strumenti digitali.",
    ],
    pullQuote:
      "Un importante riconoscimento che rappresenta non un punto di arrivo, ma una nuova partenza per continuare a promuovere innovazione, conoscenza e trasformazione digitale al servizio della comunità.",
    thanksLine:
      "Un sincero ringraziamento al quotidiano LA SICILIA per aver raccontato questa esperienza e per aver dato visibilità ai valori e ai progetti che porto avanti ogni giorno.",
  },
  // Esempio per aggiungere un futuro articolo (basta uncommentare e riempire):
  // {
  //   id: "altro-articolo-2026",
  //   outlet: "Il Sole 24 Ore",
  //   logo: "URL_LOGO",
  //   date: "...",
  //   pageRef: "...",
  //   headline: "...",
  //   image: "URL_IMMAGINE",
  //   externalUrl: "https://...",
  //   body: ["..."],
  //   pullQuote: "...",
  //   thanksLine: "...",
  // },
];

export default function PressMention() {
  const [lightbox, setLightbox] = useState(null);     // article object
  const [activeId, setActiveId] = useState(PRESS_ARTICLES[0]?.id);
  const active = PRESS_ARTICLES.find((a) => a.id === activeId) || PRESS_ARTICLES[0];
  const others = PRESS_ARTICLES.filter((a) => a.id !== activeId);

  if (!active) return null;

  return (
    <section
      data-testid="press-mention-section"
      className="py-20 md:py-28 bg-[#0B192C] text-[#F9F8F6] relative overflow-hidden"
    >
      <Newspaper
        size={520}
        strokeWidth={0.3}
        className="absolute -right-32 -bottom-20 text-[#E89B6C]/8 pointer-events-none"
      />

      <div className="relative max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-8">
            <Newspaper size={14} className="text-[#E89B6C]" />
            <span className="overline text-[#E89B6C]">La stampa parla di me</span>
          </div>

          <div className="inline-block bg-[#F9F8F6] px-8 py-5 mb-8" data-testid="press-masthead">
            <img src={active.logo} alt={active.outlet} className="h-10 md:h-14 w-auto block mx-auto" loading="lazy" />
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#0B192C]/70 mt-2 font-serif">
              {active.date} {active.pageRef ? `· ${active.pageRef}` : ""}
            </p>
          </div>

          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Il quotidiano <em className="text-[#E89B6C]">{active.outlet}</em>
            <br />
            racconta la mia storia.
          </h2>
        </div>

        <div className="grid lg:grid-cols-[1fr,1.1fr] gap-10 lg:gap-14 items-center">
          <button
            type="button"
            onClick={() => setLightbox(active)}
            data-track={`press-article-open-${active.id}`}
            data-testid="press-article-thumb"
            className="group relative block w-full overflow-hidden border-2 border-[#E89B6C]/40 hover:border-[#E89B6C] transition-all duration-300 bg-white"
          >
            <img
              src={active.image}
              alt={`Articolo ${active.outlet} — ${active.headline}`}
              className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-[#0B192C]/0 group-hover:bg-[#0B192C]/30 transition-colors duration-300 flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 inline-flex items-center gap-2 px-5 py-2.5 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em]">
                <ZoomIn size={14} /> Ingrandisci
              </span>
            </div>
            <div className="absolute top-0 left-0 bg-[#F9F8F6] px-3 py-2 flex items-center gap-2 border-r border-b border-[#0B192C]/10">
              <img src={active.logo} alt={active.outlet} className="h-4 md:h-5 w-auto" loading="lazy" />
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#0B192C]/70 font-mono">{active.date.split(" ").slice(-3).join(" ")}</span>
            </div>
          </button>

          <article className="space-y-5 text-base md:text-lg font-light leading-relaxed text-[#F9F8F6]/90">
            {active.body.map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={{
                __html: p
                  .replace(/LA SICILIA/g, '<strong class="text-[#F9F8F6] font-medium">LA SICILIA</strong>')
                  .replace(/"La Stella di E\.S\.S\.E\."/g, '<em class="text-[#E89B6C]">&ldquo;La Stella di E.S.S.E.&rdquo;</em>')
              }} />
            ))}
            {active.pullQuote && (
              <p className="border-l-2 border-[#E89B6C] pl-5 text-[#F9F8F6]">
                {active.pullQuote}
              </p>
            )}
            {active.thanksLine && (
              <p className="text-sm text-[#F9F8F6]/70">{active.thanksLine}</p>
            )}

            <div className="pt-6 border-t border-[#F9F8F6]/15">
              <p className="font-serif-display text-2xl text-[#E89B6C]">Michele Roccaro</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#F9F8F6]/60 mt-2">
                Innovazione, metodo e visione per navigare il futuro digitale.
              </p>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              <button
                onClick={() => setLightbox(active)}
                data-track={`press-article-read-${active.id}`}
                data-testid="press-article-cta"
                className="group inline-flex items-center gap-3 px-6 py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] transition-all"
              >
                Leggi l&apos;articolo completo
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </button>
              {active.externalUrl && (
                <a
                  href={active.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track={`press-article-external-${active.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 border border-[#F9F8F6]/30 text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6]/10 transition-all"
                >
                  Apri su {active.outlet} <ExternalLink size={13} />
                </a>
              )}
            </div>
          </article>
        </div>

        {/* Other press mentions strip — appears only when >1 article */}
        {others.length > 0 && (
          <div className="mt-16 pt-12 border-t border-[#F9F8F6]/15">
            <p className="overline text-[#E89B6C] mb-6 text-center">Altre apparizioni stampa</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {others.map((a) => (
                <button
                  key={a.id}
                  onClick={() => { setActiveId(a.id); document.querySelector('[data-testid="press-mention-section"]')?.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid={`press-mini-${a.id}`}
                  className="text-left bg-[#F9F8F6]/8 border border-[#F9F8F6]/15 hover:border-[#E89B6C] p-5 transition-all group"
                >
                  <div className="bg-white inline-block px-3 py-2 mb-3">
                    <img src={a.logo} alt={a.outlet} className="h-5 w-auto" loading="lazy" />
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#F9F8F6]/60">{a.date}</p>
                  <p className="font-serif-display text-lg mt-1 leading-tight">{a.headline}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#E89B6C] group-hover:gap-2 transition-all">
                    Leggi <ArrowRight size={11} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          data-testid="press-article-lightbox"
          className="fixed inset-0 z-50 bg-[#0B192C]/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 overflow-y-auto cursor-zoom-out"
        >
          <button
            onClick={() => setLightbox(null)}
            data-testid="press-article-close"
            className="absolute top-6 right-6 text-[#F9F8F6] hover:text-[#E89B6C] bg-[#0B192C]/80 p-2 border border-[#F9F8F6]/20 z-10"
            aria-label="Chiudi"
          >
            <X size={22} />
          </button>
          <img
            src={lightbox.image}
            alt={`Articolo ${lightbox.outlet} — versione ingrandita`}
            className="max-w-full h-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}
